import React, { type ReactNode } from "react";
import cx from "classnames";
import type { KeyValueItemProps } from "./components/KeyValueItem/KeyValueItem.types";

interface KeyValueListProps {
  "data-test-id"?: string;
  children: ReactNode;
  className?: string;
  withHover?: boolean;
  spaceBetween?: boolean;
  spread?: "wide" | "narrow" | "default";
  slotProps?: {
    item?: Partial<KeyValueItemProps>;
  };
}

// React.Children.toArray does not flatten fragments, so grouped children like
// `{cond && (<>...</>)}` must be walked recursively for slotProps injection.
const mapChildrenWithItemSlotProps = (
  children: ReactNode,
  itemSlotProps: Partial<KeyValueItemProps> | undefined
): ReactNode[] =>
  React.Children.toArray(children).flatMap((child) => {
    if (!React.isValidElement(child)) {
      return [child];
    }

    if (child.type === React.Fragment) {
      return mapChildrenWithItemSlotProps(
        (child.props as { children?: ReactNode }).children,
        itemSlotProps
      );
    }

    return [React.cloneElement(child, { ...itemSlotProps })];
  });

const KeyValueList = ({
  children,
  className,
  withHover,
  spaceBetween,
  spread,
  slotProps,
  ...props
}: KeyValueListProps) => {
  slotProps = {
    ...slotProps,
    item: {
      ...slotProps?.item,
      className: cx(
        spaceBetween && "flex-none!",
        spread === "wide" ? "py-1" : spread === "narrow" ? "py-[0.5px]" : "py-[2px]",
        slotProps?.item?.className
      ),
    },
  };

  return (
    <div
      className={cx("flex flex-col", className, { "with-hover": withHover })}
      style={{ minWidth: `min(calc-size(fit-content, size + 50px), 100%)` }} // add 50px to the size to account for hover-effect which increases the size of the texts, so need to make sure key & value still has some distance between them; clamp to 100% so it never overflows its container
      {...props}
    >
      {mapChildrenWithItemSlotProps(children, slotProps?.item)}
    </div>
  );
};

export default KeyValueList;
